import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useFlexibleData from "../../../../hooks/useFlexibleData";
import { useUIContext } from "../../../../context/UIContext";
import {
  DEFAULT_STUDENT_INFO_CUSTOM_SETTINGS,
  STUDENT_INFO_DIRECT_FIELD_KEYS,
  STUDENT_INFO_FIELD_TYPES,
  STUDENT_INFO_JSON_BAG_KEY,
  normalizeCustomFieldId,
  normalizeStudentInfoCustomSettings,
  parseSelectOptions,
  selectOptionsToCommaInput,
} from "../../../studentInfo/studentInfoCustomFields";

/** 입력값은 로컬 문자열로 두어 쉼표 입력 시 매 렌더마다 파싱·재조합되며 사라지는 현상을 막음 */
function SelectOptionsCommaInput({
  fieldId,
  rawRef,
  options,
  disabled,
  onCommit,
}) {
  const optsSig = JSON.stringify(options ?? []);
  const [text, setText] = useState(() => selectOptionsToCommaInput(options));

  useEffect(() => {
    setText(selectOptionsToCommaInput(JSON.parse(optsSig)));
  }, [optsSig]);

  useEffect(() => {
    const bucket = rawRef.current;
    bucket[fieldId] = text;
    return () => {
      delete bucket[fieldId];
    };
  }, [fieldId, text, rawRef]);

  const placeholder = disabled
    ? "입력 타입을 선택으로 바꾸면 입력할 수 있습니다."
    : '쉼표로 구분 (값에 쉼표가 있으면 "예시,항목" 처럼 따옴표)';

  return (
    <input
      type="text"
      className="form-control form-control-sm"
      disabled={disabled}
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => onCommit(parseSelectOptions(text))}
      placeholder={placeholder}
    />
  );
}

const buildNewJsonField = (index) => ({
  id: `custom_${Date.now()}`,
  label: "",
  enabled: true,
  type: "text",
  options: [],
  showInTable: true,
  showInModal: true,
  order:
    DEFAULT_STUDENT_INFO_CUSTOM_SETTINGS.fields.length +
    DEFAULT_STUDENT_INFO_CUSTOM_SETTINGS.jsonFields.length +
    index +
    1,
});

export default function StudentInfoFieldSettings() {
  const location = useLocation();
  const schoolId = location.pathname.split("/")[1];
  const { handleToastCenterTop, setIsLoadingModal } = useUIContext();
  const [settings, setSettings] = useState(
    DEFAULT_STUDENT_INFO_CUSTOM_SETTINGS,
  );
  const [flexibleDataId, setFlexibleDataId] = useState(null);
  const selectOptionsRawRef = useRef({});

  const {
    flexibleDataQuery: { data: flexibleData },
    createOrUpdateFlexibleData,
  } = useFlexibleData({
    schoolid: schoolId,
    main_category: "studentInfo",
    sub_category: "customFields",
  });

  useEffect(() => {
    if (flexibleData && flexibleData.length > 0) {
      setSettings(
        normalizeStudentInfoCustomSettings(flexibleData[0]?.data_value),
      );
      setFlexibleDataId(flexibleData[0]?.id || null);
      return;
    }

    setSettings(DEFAULT_STUDENT_INFO_CUSTOM_SETTINGS);
    setFlexibleDataId(null);
  }, [flexibleData]);

  const updateDirectField = (key, fieldName, value) => {
    setSettings((prev) => ({
      ...prev,
      fields: prev.fields.map((field) =>
        field.key === key ? { ...field, [fieldName]: value } : field,
      ),
    }));
  };

  const updateJsonField = (id, fieldName, value) => {
    setSettings((prev) => ({
      ...prev,
      jsonFields: prev.jsonFields.map((field) =>
        field.id === id ? { ...field, [fieldName]: value } : field,
      ),
    }));
  };

  const handleJsonIdChange = (oldId, nextId) => {
    const sanitizedId = normalizeCustomFieldId(nextId);
    updateJsonField(oldId, "id", sanitizedId);
  };

  const handleAddJsonField = () => {
    setSettings((prev) => ({
      ...prev,
      jsonFields: [
        ...prev.jsonFields,
        buildNewJsonField(prev.jsonFields.length),
      ],
    }));
  };

  const handleDeleteJsonField = (id) => {
    setSettings((prev) => ({
      ...prev,
      jsonFields: prev.jsonFields.filter((field) => field.id !== id),
    }));
  };

  const validateSettings = () => {
    const enabledFields = [
      ...settings.fields.filter((field) => field.enabled),
      ...settings.jsonFields.filter((field) => field.enabled),
    ];

    if (enabledFields.some((field) => !field.label.trim())) {
      handleToastCenterTop("활성화된 필드는 표시명을 입력해주세요.");
      return false;
    }

    const jsonIds = settings.jsonFields.map((field) => field.id);
    if (jsonIds.some((id) => !id)) {
      handleToastCenterTop("JSON 필드는 저장 키가 필요합니다.");
      return false;
    }

    if (new Set(jsonIds).size !== jsonIds.length) {
      handleToastCenterTop("JSON 필드 저장 키가 중복되었습니다.");
      return false;
    }

    if (jsonIds.includes(STUDENT_INFO_JSON_BAG_KEY)) {
      handleToastCenterTop(`${STUDENT_INFO_JSON_BAG_KEY}는 내부 저장소입니다.`);
      return false;
    }

    const reservedConflict = jsonIds.find((id) =>
      STUDENT_INFO_DIRECT_FIELD_KEYS.includes(id),
    );
    if (reservedConflict) {
      handleToastCenterTop(
        `추가 JSON 저장 키는 고정 슬롯(${STUDENT_INFO_DIRECT_FIELD_KEYS.join(", ")})과 같을 수 없습니다.`,
      );
      return false;
    }

    return true;
  };

  const buildSubmitSettings = () => ({
    version: 1,
    fields: settings.fields.map((field) => ({
      ...field,
      label: field.label.trim(),
      order: Number(field.order || 0),
      options: parseSelectOptions(
        selectOptionsRawRef.current[field.key] ??
          selectOptionsToCommaInput(field.options),
      ),
    })),
    jsonFields: settings.jsonFields.map((field) => ({
      ...field,
      id: normalizeCustomFieldId(field.id),
      label: field.label.trim(),
      order: Number(field.order || 0),
      options: parseSelectOptions(
        selectOptionsRawRef.current[field.id] ??
          selectOptionsToCommaInput(field.options),
      ),
    })),
  });

  const handleSave = () => {
    if (!validateSettings()) return;

    const submitData = {
      schoolid: schoolId,
      main_category: "studentInfo",
      sub_category: "customFields",
      data_value: buildSubmitSettings(),
    };

    if (flexibleDataId) submitData.id = flexibleDataId;

    setIsLoadingModal(true);
    createOrUpdateFlexibleData.mutate(submitData, {
      onSuccess: () => {
        setIsLoadingModal(false);
        handleToastCenterTop("학생 기본정보 필드 설정이 저장되었습니다.");
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error(error);
        handleToastCenterTop(`저장 실패: ${error.message}`);
      },
    });
  };

  const renderTypeSelect = (value, onChange) => (
    <select
      className="form-select form-select-sm"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {STUDENT_INFO_FIELD_TYPES.map((type) => (
        <option key={type.value} value={type.value}>
          {type.label}
        </option>
      ))}
    </select>
  );

  const renderSelectOptionsEditor = (
    fieldId,
    field,
    { disabled, onOptionsChange },
  ) => (
    <SelectOptionsCommaInput
      fieldId={fieldId}
      rawRef={selectOptionsRawRef}
      options={field.options}
      disabled={disabled}
      onCommit={onOptionsChange}
    />
  );

  const renderVisibilityInputs = (field, onChange) => (
    <div className="d-flex gap-2 justify-content-center">
      <label className="form-check-label">
        <input
          className="form-check-input me-1"
          type="checkbox"
          checked={field.showInTable}
          onChange={(event) => onChange("showInTable", event.target.checked)}
        />
        테이블
      </label>
      <label className="form-check-label">
        <input
          className="form-check-input me-1"
          type="checkbox"
          checked={field.showInModal}
          onChange={(event) => onChange("showInModal", event.target.checked)}
        />
        수정창
      </label>
    </div>
  );

  return (
    <>
      <div className="row mt-3">
        <div className="col-auto">
          <h3 className="fw-bold">학생 기본정보 필드 설정</h3>
        </div>
        <div className="col-auto ms-auto">
          <button
            type="button"
            className="btn btn-success"
            onClick={handleSave}
          >
            저장하기
          </button>
        </div>
      </div>
      <div className="card mt-3">
        <div className="card-header fw-bold">고정 확장 슬롯</div>
        <div className="card-body table-responsive">
          <table className="table table-sm table-bordered align-middle text-center">
            <thead className="table-light">
              <tr>
                <th style={{ width: "90px" }}>슬롯</th>
                <th style={{ width: "90px" }}>활성</th>
                <th>표시명</th>
                <th style={{ width: "130px" }}>입력 타입</th>
                <th style={{ width: "220px" }}>
                  선택 옵션
                  <div className="fw-normal text-muted small">
                    (선택형만, 쉼표 구분)
                  </div>
                </th>
                <th style={{ width: "160px" }}>노출</th>
                <th style={{ width: "90px" }}>순서</th>
              </tr>
            </thead>
            <tbody>
              {settings.fields.map((field) => (
                <tr key={field.key}>
                  <td className="fw-bold">{field.key}</td>
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={field.enabled}
                      onChange={(event) =>
                        updateDirectField(
                          field.key,
                          "enabled",
                          event.target.checked,
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={field.label}
                      onChange={(event) =>
                        updateDirectField(
                          field.key,
                          "label",
                          event.target.value,
                        )
                      }
                      placeholder="예: 보호자 직장"
                    />
                  </td>
                  <td>
                    {renderTypeSelect(field.type, (value) =>
                      updateDirectField(field.key, "type", value),
                    )}
                  </td>
                  <td>
                    {renderSelectOptionsEditor(field.key, field, {
                      disabled: field.type !== "select",
                      onOptionsChange: (options) =>
                        updateDirectField(field.key, "options", options),
                    })}
                  </td>
                  <td>
                    {renderVisibilityInputs(field, (fieldName, value) =>
                      updateDirectField(field.key, fieldName, value),
                    )}
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={field.order}
                      onChange={(event) =>
                        updateDirectField(
                          field.key,
                          "order",
                          event.target.value,
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span className="fw-bold">추가 JSON 필드</span>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={handleAddJsonField}
          >
            필드 추가
          </button>
        </div>
        <div className="card-body table-responsive">
          <table className="table table-sm table-bordered align-middle text-center">
            <thead className="table-light">
              <tr>
                <th style={{ width: "90px" }}>활성</th>
                <th style={{ width: "180px" }}>저장 키</th>
                <th>표시명</th>
                <th style={{ width: "130px" }}>입력 타입</th>
                <th style={{ width: "220px" }}>
                  선택 옵션
                  <div className="fw-normal text-muted small">
                    (선택형만, 쉼표 구분)
                  </div>
                </th>
                <th style={{ width: "160px" }}>노출</th>
                <th style={{ width: "90px" }}>순서</th>
                <th style={{ width: "70px" }}>삭제</th>
              </tr>
            </thead>
            <tbody>
              {settings.jsonFields.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-muted">
                    추가 JSON 필드가 없습니다.
                  </td>
                </tr>
              )}
              {settings.jsonFields.map((field) => (
                <tr key={field.id}>
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={field.enabled}
                      onChange={(event) =>
                        updateJsonField(
                          field.id,
                          "enabled",
                          event.target.checked,
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={field.id}
                      onChange={(event) =>
                        handleJsonIdChange(field.id, event.target.value)
                      }
                      placeholder="예: guardian_work"
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={field.label}
                      onChange={(event) =>
                        updateJsonField(field.id, "label", event.target.value)
                      }
                    />
                  </td>
                  <td>
                    {renderTypeSelect(field.type, (value) =>
                      updateJsonField(field.id, "type", value),
                    )}
                  </td>
                  <td>
                    {renderSelectOptionsEditor(field.id, field, {
                      disabled: field.type !== "select",
                      onOptionsChange: (options) =>
                        updateJsonField(field.id, "options", options),
                    })}
                  </td>
                  <td>
                    {renderVisibilityInputs(field, (fieldName, value) =>
                      updateJsonField(field.id, fieldName, value),
                    )}
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={field.order}
                      onChange={(event) =>
                        updateJsonField(field.id, "order", event.target.value)
                      }
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDeleteJsonField(field.id)}
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
